"use client";

import React, { useState, useMemo } from "react";
import { Search, Ruler, Globe, Footprints } from "lucide-react";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { cn } from "@/lib/utils";
const SIZE_DATA = [{
  us: "6",
  uk: "5.5",
  eu: "38.5",
  cm: "24.0",
  in: "9.4"
}, {
  us: "6.5",
  uk: "6",
  eu: "39",
  cm: "24.5",
  in: "9.6"
}, {
  us: "7",
  uk: "6",
  eu: "40",
  cm: "25.0",
  in: "9.8"
}, {
  us: "7.5",
  uk: "6.5",
  eu: "40.5",
  cm: "25.5",
  in: "10.0"
}, {
  us: "8",
  uk: "7",
  eu: "41",
  cm: "26.0",
  in: "10.2"
}, {
  us: "8.5",
  uk: "7.5",
  eu: "42",
  cm: "26.5",
  in: "10.4"
}, {
  us: "9",
  uk: "8",
  eu: "42.5",
  cm: "27.0",
  in: "10.6"
}, {
  us: "9.5",
  uk: "8.5",
  eu: "43",
  cm: "27.5",
  in: "10.8"
}, {
  us: "10",
  uk: "9",
  eu: "44",
  cm: "28.0",
  in: "11.0"
}, {
  us: "10.5",
  uk: "9.5",
  eu: "44.5",
  cm: "28.5",
  in: "11.2"
}, {
  us: "11",
  uk: "10",
  eu: "45",
  cm: "29.0",
  in: "11.4"
}, {
  us: "11.5",
  uk: "10.5",
  eu: "45.5",
  cm: "29.5",
  in: "11.6"
}, {
  us: "12",
  uk: "11",
  eu: "46",
  cm: "30.0",
  in: "11.8"
}, {
  us: "13",
  uk: "12",
  eu: "47.5",
  cm: "31.0",
  in: "12.2"
}, {
  us: "14",
  uk: "13",
  eu: "48.5",
  cm: "32.0",
  in: "12.6"
}];
export default function UniversalSizeMatrix() {
  const [isInches, setIsInches] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const {
    ref,
    isVisible
  } = useRevealOnScroll();
  const filteredData = useMemo(() => {
    const query = searchQuery.toLowerCase();
    return SIZE_DATA.filter(row => Object.values(row).some(val => val.toLowerCase().includes(query)));
  }, [searchQuery]);
  return <section id="universal-size-matrix" className="dark bg-background text-foreground py-24 relative overflow-hidden">
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] right-[-5%] w-[500px] h-[500px] bg-primary/5 rounded-full blur-[100px]" />
        <div className="absolute bottom-[-10%] left-[-5%] w-[500px] h-[500px] bg-secondary/5 rounded-full blur-[100px]" />
      </div>

      <div className="container mx-auto px-4 relative z-10">
        <div ref={ref} className={cn("transition-all duration-1000 transform", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10")}>
          {}
          <div className="flex flex-col md:flex-row justify-between items-end md:items-center mb-10 gap-6">
            <div className="max-w-2xl">
              <h2 className="text-4xl md:text-5xl font-bold tracking-tighter mb-4 bg-gradient-to-r from-white to-white/60 bg-clip-text text-transparent">
                Universal Size Matrix
              </h2>
              <p className="text-muted-foreground text-lg">
                Precision fit data for high-performance footwear. Compare global
                standards and find your perfect match.
              </p>
            </div>

            {}
            <div className="flex flex-col sm:flex-row gap-4 w-full md:w-auto items-stretch sm:items-center bg-card/50 p-4 rounded-xl border border-border/50 backdrop-blur-sm">
              <div className="relative w-full sm:w-64">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                <Input placeholder="Search size (e.g., 10, 44, 28)" value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="pl-9 bg-background/50 border-border focus:border-primary/50 transition-all" />
              </div>

              <div className="flex items-center justify-between sm:justify-start gap-3 pl-0 sm:pl-4 sm:border-l border-border/50">
                <span className={cn("text-sm font-medium transition-colors", !isInches ? "text-primary" : "text-muted-foreground")}>
                  CM
                </span>
                <Switch checked={isInches} onCheckedChange={setIsInches} className="data-[state=checked]:bg-secondary data-[state=unchecked]:bg-primary" />
                <span className={cn("text-sm font-medium transition-colors", isInches ? "text-secondary" : "text-muted-foreground")}>
                  IN
                </span>
              </div>
            </div>
          </div>

          {}
          <div className="rounded-xl border border-border/50 bg-card/30 backdrop-blur-md overflow-hidden shadow-2xl shadow-black/50">
            <Table>
              <TableHeader className="bg-muted/20">
                <TableRow className="hover:bg-transparent border-border/50">
                  <TableHead className="h-14 text-base font-semibold text-foreground w-1/4">
                    <div className="flex items-center gap-2">
                      <Globe className="h-4 w-4 text-primary" />
                      US Size
                    </div>
                  </TableHead>
                  <TableHead className="h-14 text-base font-semibold text-foreground w-1/4">
                    <div className="flex items-center gap-2">
                      <Globe className="h-4 w-4 text-muted-foreground" />
                      UK Size
                    </div>
                  </TableHead>
                  <TableHead className="h-14 text-base font-semibold text-foreground w-1/4">
                    <div className="flex items-center gap-2">
                      <Globe className="h-4 w-4 text-muted-foreground" />
                      EU Size
                    </div>
                  </TableHead>
                  <TableHead className="h-14 text-base font-semibold text-foreground w-1/4 text-right pr-8">
                    <div className="flex items-center justify-end gap-2">
                      <Ruler className={cn("h-4 w-4", isInches ? "text-secondary" : "text-primary")} />
                      Foot Length ({isInches ? "IN" : "CM"})
                    </div>
                  </TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredData.length > 0 ? filteredData.map((row, i) => <TableRow key={row.us} data-index={i} className="group border-border/30 transition-all duration-300 hover:bg-secondary/5 hover:border-secondary/30 hover:shadow-[0_0_30px_-10px_var(--color-secondary)] relative">
                      <TableCell className="font-mono text-lg font-medium text-foreground group-hover:text-primary transition-colors py-4">
                        {row.us}
                      </TableCell>
                      <TableCell className="font-mono text-lg text-muted-foreground group-hover:text-foreground transition-colors py-4">
                        {row.uk}
                      </TableCell>
                      <TableCell className="font-mono text-lg text-muted-foreground group-hover:text-foreground transition-colors py-4">
                        {row.eu}
                      </TableCell>
                      <TableCell className="text-right pr-8 py-4">
                        <span className={cn("font-mono text-lg font-bold px-3 py-1 rounded-md transition-all duration-300", isInches ? "bg-secondary/10 text-secondary group-hover:bg-secondary/20" : "bg-primary/10 text-primary group-hover:bg-primary/20")}>
                          {isInches ? row.in : row.cm}
                          <span className="text-xs ml-1 opacity-70">
                            {isInches ? '"' : "cm"}
                          </span>
                        </span>
                      </TableCell>
                    </TableRow>) : <TableRow>
                    <TableCell colSpan={4} className="h-32 text-center text-muted-foreground">
                      <div className="flex flex-col items-center justify-center gap-2">
                        <Footprints className="h-8 w-8 opacity-20" />
                        <p>No sizes found matching "{searchQuery}"</p>
                      </div>
                    </TableCell>
                  </TableRow>}
              </TableBody>
            </Table>
          </div>

          {}
          <div className="mt-6 flex items-start gap-3 text-sm text-muted-foreground bg-muted/10 p-4 rounded-lg border border-border/30">
            <div className="mt-1 min-w-4 h-4 rounded-full bg-primary/20 flex items-center justify-center">
              <div className="w-1.5 h-1.5 rounded-full bg-primary" />
            </div>
            <p>
              <span className="text-foreground font-medium">Pro Tip:</span> For
              performance footwear, we recommend selecting a size that allows
              approximately 0.5cm (0.2") of space at the toe. If you fall
              between sizes, opt for the larger size for better circulation
              during high-intensity activities.
            </p>
          </div>
        </div>
      </div>
    </section>;
}